import React from 'react';
import ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { installNativeLinkGuard } from './utils/openExternal';
import { installKeyboardTabBar } from './utils/keyboardTabBar';
import { installAppRefresh } from './utils/appRefresh';
import { installUniversalLinks } from './utils/universalLinks';
import GetTheAppBanner from './components/GetTheAppBanner';
import LegalPageFrame from './components/LegalPageFrame';
import './index.css';
import App from './App';
import CookieConsent from './components/CookieConsent';
import { ThemeProvider } from './ThemeContext';
import { FeatureFlagProvider } from './hooks/useFeatureFlag';
import { rememberThemeChoice } from './shared/themePreference';
import NotificationSettingsPreview, { isNotificationPreviewPath, primePreviewTheme } from './preview/NotificationSettingsPreview';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { installClientErrorReporter } from './shared/clientErrorReport';
import { queryPreviewAllowed } from './shared/devPreview';

if (typeof window !== 'undefined' && isNotificationPreviewPath(window.location.pathname)) {
  primePreviewTheme(window.localStorage, window.location.search);
}

  if (process.env.NODE_ENV === 'development'
  && queryPreviewAllowed(process.env.NODE_ENV, Capacitor.isNativePlatform())
  && typeof window !== 'undefined'
  && (window.location.search.indexOf('preview=agent-home') !== -1
    || window.location.search.indexOf('preview=ai-chat') !== -1
    || window.location.search.indexOf('preview=invite-friend') !== -1
    || window.location.search.indexOf('preview=team') !== -1
    || window.location.search.indexOf('preview=imports') !== -1
    || window.location.search.indexOf('preview=plan-welcome') !== -1)) {
  const theme = new URLSearchParams(window.location.search).get('theme');
  if (theme === 'dark' || theme === 'light') rememberThemeChoice(window.localStorage, theme === 'dark');
  if (window.location.search.indexOf('preview=plan-welcome') !== -1
    && window.location.search.indexOf('shell=native') !== -1) {
    document.documentElement.classList.add('native-app');
  }
}

let HomeSearchPreview = null;
let PulsePreview = null;
let SuperAdminPreview = null;
let CategoryFilterPreview = null;
let CeoPreview = null;
let AdminEmailCodePreview = null;
let VendorCardPreview = null;
let AgentProjectsPreview = null;
let HomeownerProjectPreview = null;
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  const previewPath = (String(window.location.pathname || '').replace(/\/+$/, '') || '/');
  const theme = new URLSearchParams(window.location.search).get('theme');
  if (previewPath === '/preview/home-search' || previewPath === '/preview/pulse' || previewPath === '/preview/super-admin' || previewPath === '/preview/categories' || previewPath === '/preview/ceo' || previewPath === '/preview/admin-email-code' || previewPath === '/preview/vendor-card' || previewPath === '/preview/agent-projects' || previewPath === '/preview/homeowner-project') {
    if (theme === 'dark' || theme === 'light') rememberThemeChoice(window.localStorage, theme === 'dark');
  }
  if (previewPath === '/preview/home-search') {
    HomeSearchPreview = require('./preview/HomeSearchPreview').default;
  }
  if (previewPath === '/preview/pulse') {
    PulsePreview = require('./preview/PulsePreview').default;
  }
  if (previewPath === '/preview/super-admin') {
    SuperAdminPreview = require('./preview/SuperAdminPreview').default;
  }
  if (previewPath === '/preview/categories') {
    CategoryFilterPreview = require('./preview/CategoryFilterPreview').default;
  }
  if (previewPath === '/preview/ceo') {
    CeoPreview = require('./preview/CeoPreview').default;
  }
  if (previewPath === '/preview/admin-email-code') {
    AdminEmailCodePreview = require('./preview/AdminEmailCodePreview').default;
  }
  if (previewPath === '/preview/vendor-card') {
    VendorCardPreview = require('./preview/VendorCardPreview').default;
  }
  if (previewPath === '/preview/agent-projects') {
    AgentProjectsPreview = require('./preview/AgentProjectsPreview').default;
  }
  if (previewPath === '/preview/homeowner-project') {
    HomeownerProjectPreview = require('./preview/HomeownerProjectPreview').default;
  }
}

let HomeProfilePreview = null;
let DocumentsPreview = null;
let HomeFactsPreview = null;
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  const previewPath = (String(window.location.pathname || '').replace(/\/+$/, '') || '/');
  const theme = new URLSearchParams(window.location.search).get('theme');
  if (previewPath === '/preview/home-facts') {
    HomeFactsPreview = require('./preview/HomeFactsPreview').default;
  }
  if (previewPath === '/preview/home-profile' || previewPath === '/preview/documents' || previewPath === '/preview/homeowner-project') {
    if (theme === 'dark' || theme === 'light') rememberThemeChoice(window.localStorage, theme === 'dark');
  }
  if (previewPath === '/preview/home-profile') {
    HomeProfilePreview = require('./preview/HomeProfilePreview').default;
  }
  if (previewPath === '/preview/documents') {
    DocumentsPreview = require('./preview/DocumentsPreview').default;
  }
}

// Marks the Capacitor web view. Phone pages scroll the document. The class
// still lets the assistant keep one list, and it still hides the site footer.
// Keyboard resize stays "native" (see capacitor.config.ts) so the web view
// shrinks and a sheet can scroll. The tab bar is hidden while that keyboard
// is open; otherwise bottom:0 rides up and sits above the keys.
const nativeKeyboard = Capacitor.isNativePlatform();
if (nativeKeyboard) {
  document.documentElement.classList.add('native-app');
  installNativeLinkGuard();
  Keyboard.setResizeMode({ mode: 'native' }).catch(() => {});
}
installKeyboardTabBar({
  native: nativeKeyboard,
  Keyboard: nativeKeyboard ? Keyboard : null,
});
installAppRefresh();
installUniversalLinks();
installClientErrorReporter({ native: nativeKeyboard });

const notificationPreview = typeof window !== 'undefined' && isNotificationPreviewPath(window.location.pathname);
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      {HomeFactsPreview ? <HomeFactsPreview /> : HomeSearchPreview ? <HomeSearchPreview /> : HomeProfilePreview ? <HomeProfilePreview /> : DocumentsPreview ? <DocumentsPreview /> : PulsePreview ? <PulsePreview /> : CategoryFilterPreview ? <CategoryFilterPreview /> : SuperAdminPreview ? <SuperAdminPreview /> : CeoPreview ? <CeoPreview /> : AdminEmailCodePreview ? <AdminEmailCodePreview /> : VendorCardPreview ? <VendorCardPreview /> : AgentProjectsPreview ? <AgentProjectsPreview /> : HomeownerProjectPreview ? <HomeownerProjectPreview /> : notificationPreview ? <NotificationSettingsPreview /> : (
        <LegalPageFrame>
          <GetTheAppBanner />
          <div className="app-below-banner">
            <FeatureFlagProvider>
              <App />
            </FeatureFlagProvider>
          </div>
          <CookieConsent />
        </LegalPageFrame>
      )}
    </ThemeProvider>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// DISABLED for Capacitor - service workers cause blank screen in native apps
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example, pass a function that prints each metric)
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
